import React, { useEffect, useRef, useState } from 'react';

const parseLocalDate = (dateVal) => {
    if (!dateVal) return null;
    const datePart = String(dateVal).split('T')[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return new Date(year, month - 1, day);
};

/** Backend may store only a year (e.g. "2020"); type="date" requires YYYY-MM-DD on iOS/Android. */
export const normalizeDateInputValue = (dateVal) => {
    if (!dateVal) return '';
    const str = String(dateVal).trim().split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (/^\d{4}-\d{2}$/.test(str)) return `${str}-01`;
    if (/^\d{4}$/.test(str)) return `${str}-01-01`;
    return '';
};

const formatDisplay = (dateVal) => {
    const date = parseLocalDate(dateVal);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

/** iOS Safari does not support showPicker(); touch devices work best with a visible native date input. */
const shouldUseNativeDateInput = () => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/i.test(ua)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);
    const isCoarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches;
    return isIOS || isAndroid || Boolean(isCoarsePointer);
};

/** Desktop formatted display needs showPicker(); otherwise use a visible native input (Safari 15, older Firefox). */
const supportsShowPicker = () => (
    typeof HTMLInputElement !== 'undefined'
    && typeof HTMLInputElement.prototype.showPicker === 'function'
);

export const DatePickerWithMonthName = ({
    value,
    onChange,
    max,
    min,
    placeholder,
    className = 'form-control',
    id,
    name,
    required,
}) => {
    const inputRef = useRef(null);
    const [useNativeInput, setUseNativeInput] = useState(shouldUseNativeDateInput);
    const [useFormattedDesktop, setUseFormattedDesktop] = useState(
        () => !shouldUseNativeDateInput() && supportsShowPicker()
    );
    const normalizedValue = normalizeDateInputValue(value);

    useEffect(() => {
        const native = shouldUseNativeDateInput();
        setUseNativeInput(native);
        setUseFormattedDesktop(!native && supportsShowPicker());
    }, []);

    const openNativePicker = () => {
        const el = inputRef.current;
        if (!el) return;
        // Chrome/Edge on Windows open the calendar via showPicker(), not focus on a hidden overlay input.
        if (typeof el.showPicker === 'function') {
            try {
                el.showPicker();
                return;
            } catch {
                // showPicker can throw without a user gesture or on unsupported browsers
            }
        }
        el.focus({ preventScroll: true });
        el.click();
    };

    const nativeDateInput = (
        <input
            ref={inputRef}
            type="date"
            id={id}
            name={name}
            required={required}
            className={className}
            value={normalizedValue}
            onChange={onChange}
            max={max}
            min={min}
            placeholder={placeholder || 'Select date'}
            aria-label={placeholder || 'Select date'}
            style={{
                fontSize: '16px',
                minHeight: '40px',
                WebkitAppearance: 'none',
                appearance: 'none',
            }}
        />
    );

    // Mobile/tablet + browsers without showPicker: visible <input type="date"> (value/onChange stay YYYY-MM-DD).
    if (useNativeInput || !useFormattedDesktop) {
        return <div className="date-picker-month-name w-100">{nativeDateInput}</div>;
    }

    return (
        <div className="date-picker-month-name position-relative w-100" style={{ minHeight: '40px' }}>
            <input
                type="text"
                className={className}
                value={formatDisplay(normalizedValue)}
                placeholder={placeholder || 'Select date'}
                readOnly
                tabIndex={0}
                role="button"
                aria-label={placeholder || 'Select date'}
                onClick={openNativePicker}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openNativePicker();
                    }
                }}
                style={{
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    width: '100%',
                }}
            />
            {/* Off-screen date input: overlay at opacity 0.01 blocked Chrome Windows calendar; showPicker() on click instead. */}
            <input
                ref={inputRef}
                type="date"
                name={name}
                required={required}
                value={normalizedValue}
                onChange={onChange}
                max={max}
                min={min}
                id={id}
                tabIndex={-1}
                aria-hidden="true"
                className="date-picker-month-name__native"
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    border: 0,
                    padding: 0,
                    margin: 0,
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
};
